import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  NotificationsActive as NotificationsActiveIcon,
  Backup as BackupIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Key as KeyIcon,
  Lock as LockIcon,
  Add as AddIcon,
  SupportAgent,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { loadCompanyProfile, saveCompanyProfile } from '../utils/companyProfileStorage';
import { authService } from '../services/api';

const getUserScopedStorageKey = (baseKey) => {
  const user = authService.getCurrentUserFromStorage();
  const identifier = user?.id || user?.email;
  return identifier ? `${baseKey}:${identifier}` : baseKey;
};

const Settings = () => {
  const ENABLE_TWO_FACTOR_SETTINGS = false;
  const settingsStorageKey = getUserScopedStorageKey('appSettings');
  const passwordStorageKey = getUserScopedStorageKey('appPassword');

  const [settings, setSettings] = useState({
    autoBackup: true,
    sessionTimeout: 30,
    requirePassword: false,
    password: '',
    remindersEnabled: true,
    companyInfo: {
      name: '',
      address: '',
      phone: '',
      email: '',
      siret: '',
      vatNumber: ''
    },
    securitySettings: {
      lockAfterInactivity: true,
      inactivityMinutes: 15,
      showSensitiveInfo: false
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openBackupDialog, setOpenBackupDialog] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [selectedAdminUserId, setSelectedAdminUserId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [impersonation, setImpersonation] = useState(authService.getImpersonation());
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamForm, setTeamForm] = useState({
    name: '',
    email: '',
    password: '',
    teamRole: 'MEMBER'
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorSetupDialogOpen, setTwoFactorSetupDialogOpen] = useState(false);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState({ qrCodeDataUrl: '', manualEntryKey: '' });
  const [twoFactorVerifyCode, setTwoFactorVerifyCode] = useState('');
  const [twoFactorBackupCodes, setTwoFactorBackupCodes] = useState([]);

  useEffect(() => {
    // Charger le profil de l'entreprise depuis le storage utilisateur
    const companyProfile = loadCompanyProfile();
    const storedPassword = localStorage.getItem(passwordStorageKey);
    const hasLocalPasswordProtection = Boolean(storedPassword);
    
    // Charger les paramètres depuis localStorage
    const savedSettings = localStorage.getItem(settingsStorageKey);
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings({
        ...parsed,
        requirePassword: hasLocalPasswordProtection || Boolean(parsed.requirePassword),
        companyInfo: companyProfile || parsed.companyInfo
      });
    } else if (companyProfile) {
      setSettings(prev => ({
        ...prev,
        requirePassword: hasLocalPasswordProtection,
        companyInfo: companyProfile
      }));
    } else if (hasLocalPasswordProtection) {
      setSettings(prev => ({
        ...prev,
        requirePassword: true
      }));
    }

    // Charger la préférence des relances depuis l'API
    authService.getReminderSettings()
      .then((data) => {
        if (typeof data.remindersEnabled === 'boolean') {
          setSettings(prev => ({ ...prev, remindersEnabled: data.remindersEnabled }));
        }
      })
      .catch(() => {
        // silencieux : fallback localStorage
      });
  }, [settingsStorageKey, passwordStorageKey]);

  useEffect(() => {
    const currentUser = authService.getCurrentUserFromStorage();
    setIsAdmin(currentUser?.role === 'ADMIN');
    setImpersonation(authService.getImpersonation());
  }, []);

  useEffect(() => {
    if (!ENABLE_TWO_FACTOR_SETTINGS) {
      return;
    }

    const loadTwoFactorStatus = async () => {
      try {
        const status = await authService.getTwoFactorStatus();
        setTwoFactorEnabled(Boolean(status.enabled));
      } catch (error) {
        // silencieux
      }
    };
    loadTwoFactorStatus();
  }, []);

  const currentUser = authService.getCurrentUserFromStorage();
  const canManageTeam = currentUser?.role === 'ADMIN' || currentUser?.teamRole === 'OWNER' || !currentUser?.accountId;

  const loadTeamMembers = useCallback(async () => {
    if (!canManageTeam) {
      setTeamError('Accès refusé (propriétaire requis)');
      return;
    }
    setTeamLoading(true);
    setTeamError('');
    try {
      const members = await authService.getTeamMembers();
      setTeamMembers(members);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        setTeamError('Accès refusé (propriétaire requis)');
      } else if (status === 401) {
        setTeamError('Session expirée. Merci de vous reconnecter.');
      } else {
        setTeamError('Impossible de charger l\'équipe');
      }
    } finally {
      setTeamLoading(false);
    }
  }, [canManageTeam]);

  const handleCreateTeamMember = async () => {
    const normalizedEmail = (teamForm.email || '').trim().toLowerCase();

    if (!teamForm.name || !normalizedEmail || !teamForm.password) {
      setTeamError('Nom, email et mot de passe requis');
      return;
    }

    if ((currentUser?.email || '').toLowerCase() === normalizedEmail) {
      setTeamError('Cet email est déjà utilisé par votre compte. Utilisez un email différent pour le membre.');
      return;
    }

    if (teamMembers?.some(m => (m.email || '').toLowerCase() === normalizedEmail)) {
      setTeamError('Un membre avec cet email est déjà présent dans l\'équipe.');
      return;
    }

    setTeamLoading(true);
    setTeamError('');
    try {
      await authService.createTeamMember({
        ...teamForm,
        email: normalizedEmail
      });
      setTeamDialogOpen(false);
      setTeamForm({ name: '', email: '', password: '', teamRole: 'MEMBER' });
      await loadTeamMembers();
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const serverText = data?.message || data?.error;

      if (status === 401) {
        setTeamError('Session expirée. Merci de vous reconnecter.');
      } else if (status === 403) {
        setTeamError(serverText || 'Accès refusé');
      } else if (status === 400) {
        setTeamError(serverText || 'Données invalides');
      } else if (error?.message?.includes('Network') || error?.code === 'ERR_NETWORK') {
        setTeamError('Serveur indisponible. Vérifiez que le backend (port 5000) est démarré.');
      } else {
        setTeamError(serverText || 'Erreur lors de la création du membre');
      }
    } finally {
      setTeamLoading(false);
    }
  };

  const handleUpdateTeamRole = async (memberId, teamRole) => {
    setTeamLoading(true);
    setTeamError('');
    try {
      await authService.updateTeamMember(memberId, { teamRole });
      setTeamMembers(prev => prev.map(member => member.id === memberId ? { ...member, teamRole } : member));
    } catch (error) {
      setTeamError('Erreur lors de la mise à jour du rôle');
    } finally {
      setTeamLoading(false);
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    setTeamLoading(true);
    setTeamError('');
    try {
      await authService.deleteTeamMember(memberId);
      setTeamMembers(prev => prev.filter(member => member.id !== memberId));
    } catch (error) {
      setTeamError('Erreur lors de la suppression du membre');
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    if (canManageTeam) {
      loadTeamMembers();
    }
  }, [canManageTeam, loadTeamMembers]);

  const loadAdminUsers = async (searchOverride) => {
    if (!isAdmin) {
      setAdminError('Accès administrateur requis. Revenez à votre compte admin.');
      return;
    }
    setAdminLoading(true);
    setAdminError('');
    try {
      const search = typeof searchOverride === 'string' ? searchOverride : adminSearch;
      const users = await authService.adminListUsers(search);

      const normalize = (value) => (value || '').toString().trim().toLowerCase();
      const sorted = (users || []).slice().sort((a, b) => {
        const nameA = normalize(a?.name);
        const nameB = normalize(b?.name);
        const emailA = normalize(a?.email);
        const emailB = normalize(b?.email);

        const keyA = nameA || emailA;
        const keyB = nameB || emailB;

        const byName = keyA.localeCompare(keyB, 'fr', { sensitivity: 'base' });
        if (byName !== 0) return byName;
        return emailA.localeCompare(emailB, 'fr', { sensitivity: 'base' });
      });

      setAdminUsers(sorted);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        setAdminError('Session expirée. Merci de vous reconnecter.');
      } else if (status === 403) {
        setAdminError('Accès administrateur requis.');
      } else if (error?.message?.includes('Network') || error?.code === 'ERR_NETWORK') {
        setAdminError('Serveur indisponible. Vérifiez que le backend (port 5000) est démarré.');
      } else {
        setAdminError('Impossible de charger les utilisateurs');
      }
    } finally {
      setAdminLoading(false);
    }
  };

  const handleImpersonate = async (userId) => {
    if (!isAdmin) {
      setAdminError('Accès administrateur requis. Revenez à votre compte admin.');
      return;
    }
    setAdminLoading(true);
    setAdminError('');
    try {
      await authService.adminImpersonate(userId);
      setImpersonation(authService.getImpersonation());
      window.location.href = '/dashboard';
    } catch (error) {
      setAdminError('Impossible de se connecter au compte sélectionné');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleStopImpersonation = () => {
    const restored = authService.stopImpersonation();
    if (restored) {
      setImpersonation(null);
      window.location.href = '/dashboard';
    } else {
      setAdminError('Aucun compte admin à restaurer');
    }
  };

  const saveSettings = async () => {
    localStorage.setItem(settingsStorageKey, JSON.stringify(settings));
    
    // Sauvegarder également les informations de l'entreprise dans le profil,
    // sans écraser les couleurs/thème déjà configurés.
    const existingProfile = loadCompanyProfile() || {};
    saveCompanyProfile({
      ...existingProfile,
      ...settings.companyInfo,
      primaryColor: existingProfile.primaryColor,
      secondaryColor: existingProfile.secondaryColor,
      documentTheme: existingProfile.documentTheme,
      logo: existingProfile.logo,
      quoteColumns: existingProfile.quoteColumns,
      catalogItems: existingProfile.catalogItems,
    });

    // Informer l'app que les réglages ont changé (ex: timer d'inactivité)
    window.dispatchEvent(new Event('appSettingsUpdated'));

    // Sauvegarder la préférence des relances côté serveur
    try {
      await authService.updateReminderSettings(settings.remindersEnabled);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour des relances automatiques' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    
    setMessage({ type: 'success', text: 'Paramètres sauvegardés avec succès' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleSettingChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handlePasswordChange = () => {
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }
    
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 8 caractères' });
      return;
    }

    // Vérifier le mot de passe actuel SEULEMENT s'il en existe déjà un
    const currentStoredPassword = localStorage.getItem(passwordStorageKey);
    if (currentStoredPassword) {
      // Si un mot de passe existe, vérifier que le mot de passe actuel est correct
      if (!currentPassword) {
        setMessage({ type: 'error', text: 'Veuillez saisir votre mot de passe actuel' });
        return;
      }
      if (currentStoredPassword !== currentPassword) {
        setMessage({ type: 'error', text: 'Mot de passe actuel incorrect' });
        return;
      }
    }

    // Sauvegarder le nouveau mot de passe
    localStorage.setItem(passwordStorageKey, newPassword);
    setSettings(prev => ({ ...prev, requirePassword: true, password: newPassword }));
    
    setOpenPasswordDialog(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setMessage({ type: 'success', text: currentStoredPassword ? 'Mot de passe modifié avec succès' : 'Protection par mot de passe activée' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const exportData = () => {
    const allData = {
      companies: JSON.parse(localStorage.getItem('companies') || '[]'),
      quotes: JSON.parse(localStorage.getItem('quotes') || '[]'),
      invoices: JSON.parse(localStorage.getItem('invoices') || '[]'),
      settings: settings,
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(allData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `sg-digital-backup-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    setMessage({ type: 'success', text: 'Sauvegarde exportée avec succès' });
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        
        // Vérifier la structure des données
        if (importedData.companies && importedData.quotes && importedData.invoices) {
          localStorage.setItem('companies', JSON.stringify(importedData.companies));
          localStorage.setItem('quotes', JSON.stringify(importedData.quotes));
          localStorage.setItem('invoices', JSON.stringify(importedData.invoices));
          
          if (importedData.settings) {
            setSettings(importedData.settings);
            localStorage.setItem(settingsStorageKey, JSON.stringify(importedData.settings));
          }
          
          setMessage({ type: 'success', text: 'Données importées avec succès. Rechargez la page pour voir les changements.' });
        } else {
          setMessage({ type: 'error', text: 'Format de fichier invalide' });
        }
      } catch (error) {
        setMessage({ type: 'error', text: 'Erreur lors de l\'import des données' });
      }
    };
    reader.readAsText(file);
    setOpenBackupDialog(false);
  };

  const clearAllData = () => {
    if (window.confirm('Êtes-vous sûr de vouloir effacer toutes les données ? Cette action est irréversible.')) {
      localStorage.removeItem('companies');
      localStorage.removeItem('quotes');
      localStorage.removeItem('invoices');
      localStorage.removeItem(settingsStorageKey);
      localStorage.removeItem(passwordStorageKey);
      setMessage({ type: 'warning', text: 'Toutes les données ont été supprimées. Rechargez la page.' });
    }
  };

  const canAdminSupport = isAdmin || Boolean(impersonation);

  const handleEnableTwoFactorStart = async () => {
    setTwoFactorLoading(true);
    try {
      const data = await authService.setupTwoFactor();
      setTwoFactorSetupData({
        qrCodeDataUrl: data.qrCodeDataUrl || '',
        manualEntryKey: data.manualEntryKey || ''
      });
      setTwoFactorVerifyCode('');
      setTwoFactorBackupCodes([]);
      setTwoFactorSetupDialogOpen(true);
    } catch (error) {
      setMessage({ type: 'error', text: 'Impossible d\'initialiser la 2FA' });
      setTwoFactorEnabled(false);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleConfirmEnableTwoFactor = async () => {
    if (!twoFactorVerifyCode) {
      setMessage({ type: 'error', text: 'Veuillez saisir le code 2FA' });
      return;
    }

    setTwoFactorLoading(true);
    try {
      const data = await authService.enableTwoFactor(twoFactorVerifyCode);
      setTwoFactorEnabled(true);
      setTwoFactorBackupCodes(data.backupCodes || []);
      setMessage({ type: 'success', text: '2FA activée. Sauvegardez vos codes de secours.' });
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.error || 'Code 2FA invalide' });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    const accountPassword = window.prompt('Entrez votre mot de passe de compte pour désactiver la 2FA :');
    if (!accountPassword) {
      setTwoFactorEnabled(true);
      return;
    }

    setTwoFactorLoading(true);
    try {
      await authService.disableTwoFactor(accountPassword);
      setTwoFactorEnabled(false);
      setMessage({ type: 'success', text: '2FA désactivée' });
    } catch (error) {
      setTwoFactorEnabled(true);
      setMessage({ type: 'error', text: error?.response?.data?.error || 'Impossible de désactiver la 2FA' });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleTwoFactorToggle = (enabled) => {
    if (enabled) {
      handleEnableTwoFactorStart();
      return;
    }
    handleDisableTwoFactor();
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* En-tête */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Paramètres et Sécurité
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configurez votre application et gérez la sécurité de vos données
        </Typography>
      </Box>

      {/* Messages */}
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Informations de l'entreprise */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Informations de l'entreprise</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nom de l'entreprise"
                    value={settings.companyInfo.name}
                    onChange={(e) => handleSettingChange('companyInfo', 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Adresse"
                    multiline
                    rows={2}
                    value={settings.companyInfo.address}
                    onChange={(e) => handleSettingChange('companyInfo', 'address', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Téléphone"
                    value={settings.companyInfo.phone}
                    onChange={(e) => handleSettingChange('companyInfo', 'phone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={settings.companyInfo.email}
                    onChange={(e) => handleSettingChange('companyInfo', 'email', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SIRET"
                    value={settings.companyInfo.siret}
                    onChange={(e) => handleSettingChange('companyInfo', 'siret', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Numéro de TVA"
                    value={settings.companyInfo.vatNumber}
                    onChange={(e) => handleSettingChange('companyInfo', 'vatNumber', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sécurité */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Sécurité</Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Protection par mot de passe"
                    secondary="Protégez l'accès à votre application"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.requirePassword}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setOpenPasswordDialog(true);
                        } else {
                          setSettings(prev => ({ ...prev, requirePassword: false }));
                          localStorage.removeItem(passwordStorageKey);
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemText 
                    primary="Verrouillage automatique"
                    secondary="Verrouille l'app après inactivité"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.securitySettings.lockAfterInactivity}
                      onChange={(e) => handleSettingChange('securitySettings', 'lockAfterInactivity', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary="Délai d'inactivité (minutes)"
                    secondary="Temps avant verrouillage automatique"
                  />
                  <TextField
                    type="number"
                    size="small"
                    sx={{ width: 80 }}
                    value={settings.securitySettings.inactivityMinutes}
                    onChange={(e) => handleSettingChange('securitySettings', 'inactivityMinutes', parseInt(e.target.value))}
                  />
                </ListItem>

                {ENABLE_TWO_FACTOR_SETTINGS && (
                  <>
                    <Divider />

                    <ListItem>
                      <ListItemText
                        primary="Authentification à 2 facteurs (2FA)"
                        secondary="Code TOTP via Google Authenticator, Aegis ou Authy"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={twoFactorEnabled}
                          disabled={twoFactorLoading}
                          onChange={(e) => handleTwoFactorToggle(e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </>
                )}

                {settings.requirePassword && (
                  <>
                    <Divider />
                    <ListItem>
                      <Button
                        variant="outlined"
                        startIcon={<KeyIcon />}
                        onClick={() => setOpenPasswordDialog(true)}
                        fullWidth
                      >
                        Modifier le mot de passe
                      </Button>
                    </ListItem>
                  </>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Relances automatiques */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsActiveIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Relances automatiques</Typography>
              </Box>

              <List>
                <ListItem>
                  <ListItemText
                    primary="Activer les relances automatiques"
                    secondary="Envoie des emails de relance aux clients en retard (PRO uniquement)"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.remindersEnabled}
                      onChange={(e) => setSettings(prev => ({ ...prev, remindersEnabled: e.target.checked }))}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {canAdminSupport && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SupportAgent sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6">Support administrateur</Typography>
                </Box>

                {impersonation && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Mode support actif — connecté en tant que {currentUser?.email || 'utilisateur'}.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Rechercher un utilisateur"
                    value={adminSearch}
                    onChange={(e) => setAdminSearch(e.target.value)}
                    size="small"
                    sx={{ minWidth: 240 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={loadAdminUsers}
                    disabled={adminLoading}
                  >
                    {adminLoading ? 'Chargement...' : 'Charger'}
                  </Button>
                  {impersonation && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleStopImpersonation}
                    >
                      Revenir à mon compte admin
                    </Button>
                  )}
                </Box>

                {adminError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {adminError}
                  </Alert>
                )}

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="admin-user-select-label">Utilisateur</InputLabel>
                  <Select
                    labelId="admin-user-select-label"
                    label="Utilisateur"
                    value={selectedAdminUserId}
                    onChange={(e) => setSelectedAdminUserId(e.target.value)}
                    disabled={adminLoading}
                    onOpen={() => loadAdminUsers(adminSearch)}
                  >
                    {adminLoading ? (
                      <MenuItem value="" disabled>
                        Chargement…
                      </MenuItem>
                    ) : null}

                    {(!adminLoading && adminUsers.length === 0) ? (
                      <MenuItem value="" disabled>
                        Cliquez pour charger la liste
                      </MenuItem>
                    ) : null}

                    {adminUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id} disabled={user.role === 'ADMIN'}>
                        {`${user.name || 'Sans nom'} — ${user.email}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {!adminLoading && adminUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Cliquez sur la liste “Utilisateur” pour charger tous les comptes (triés A→Z).
                  </Typography>
                )}

                <Button
                  variant="contained"
                  onClick={() => handleImpersonate(selectedAdminUserId)}
                  disabled={adminLoading || !selectedAdminUserId}
                  sx={{
                    background: 'linear-gradient(135deg, #7A7068 0%, #8B8178 52%, #5E564F 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #6B625B 0%, #7A7068 52%, #4F4741 100%)'
                    },
                    '&.Mui-disabled': {
                      background: '#C8C0B8',
                      color: '#5E564F'
                    }
                  }}
                >
                  Se connecter
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {canManageTeam && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Collaboration & équipe</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={loadTeamMembers} disabled={teamLoading}>
                      {teamLoading ? 'Chargement...' : 'Actualiser'}
                    </Button>
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setTeamDialogOpen(true)}>
                      Ajouter
                    </Button>
                  </Box>
                </Box>

                {teamError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {teamError}
                  </Alert>
                )}

                {teamMembers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Aucun membre d'équipe.
                  </Typography>
                ) : (
                  <List>
                    {teamMembers.map((member) => (
                      <ListItem key={member.id} divider>
                        <ListItemText
                          primary={`${member.name || 'Sans nom'} — ${member.email}`}
                          secondary={member.teamRole === 'OWNER' ? 'Propriétaire' : `Rôle: ${member.teamRole}`}
                        />
                        {member.teamRole !== 'OWNER' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small">
                              <Select
                                value={member.teamRole}
                                onChange={(e) => handleUpdateTeamRole(member.id, e.target.value)}
                              >
                                <MenuItem value="MEMBER">Membre</MenuItem>
                                <MenuItem value="MANAGER">Manager</MenuItem>
                              </Select>
                            </FormControl>
                            <IconButton color="error" onClick={() => handleDeleteTeamMember(member.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Sauvegarde et données */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BackupIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">Gestion des données</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={exportData}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Exporter les données
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Téléchargez toutes vos données
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => setOpenBackupDialog(true)}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Importer des données
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Restaurez depuis une sauvegarde
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={clearAllData}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Effacer les données
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Supprime toutes les données
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoBackup}
                    onChange={(e) => setSettings(prev => ({ ...prev, autoBackup: e.target.checked }))}
                  />
                }
                label="Sauvegarde automatique quotidienne"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bouton de sauvegarde principal */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={saveSettings}
          sx={{ minWidth: 200 }}
        >
          Sauvegarder les paramètres
        </Button>
      </Box>

      {/* Dialog ajout membre équipe */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un membre</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom"
                value={teamForm.name}
                onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={teamForm.email}
                onChange={(e) => setTeamForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mot de passe"
                type="password"
                value={teamForm.password}
                onChange={(e) => setTeamForm(prev => ({ ...prev, password: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="team-role-label">Rôle</InputLabel>
                <Select
                  labelId="team-role-label"
                  label="Rôle"
                  value={teamForm.teamRole}
                  onChange={(e) => setTeamForm(prev => ({ ...prev, teamRole: e.target.value }))}
                >
                  <MenuItem value="MEMBER">Membre</MenuItem>
                  <MenuItem value="MANAGER">Manager</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)} disabled={teamLoading}>Annuler</Button>
          <Button onClick={handleCreateTeamMember} variant="contained" disabled={teamLoading}>
            {teamLoading ? 'Création...' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog changement de mot de passe */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ mr: 1 }} />
            Paramètres du mot de passe
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {localStorage.getItem(passwordStorageKey) && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mot de passe actuel"
                  type={showPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    )
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nouveau mot de passe"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Minimum 8 caractères"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirmer le mot de passe"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Annuler</Button>
          <Button onClick={handlePasswordChange} variant="contained">
            {settings.requirePassword ? 'Modifier' : 'Activer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog import de données */}
      <Dialog open={openBackupDialog} onClose={() => setOpenBackupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importer des données</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Sélectionnez un fichier de sauvegarde pour restaurer vos données.
          </Typography>
          <input
            type="file"
            accept=".json"
            onChange={importData}
            style={{ width: '100%' }}
          />
          <Alert severity="warning" sx={{ mt: 2 }}>
            Cette action remplacera toutes vos données actuelles. Assurez-vous d'avoir fait une sauvegarde.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBackupDialog(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>

      {ENABLE_TWO_FACTOR_SETTINGS && (
        <Dialog
          open={twoFactorSetupDialogOpen}
          onClose={() => {
            if (!twoFactorBackupCodes.length) {
              setTwoFactorEnabled(false);
            }
            setTwoFactorSetupDialogOpen(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Activer la 2FA</DialogTitle>
          <DialogContent>
            {twoFactorBackupCodes.length === 0 ? (
              <>
                <Typography sx={{ mb: 2 }}>
                  Scannez ce QR code avec votre application d'authentification puis saisissez le code à 6 chiffres.
                </Typography>

                {twoFactorSetupData.qrCodeDataUrl ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <img src={twoFactorSetupData.qrCodeDataUrl} alt="QR Code 2FA" style={{ width: 200, height: 200 }} />
                  </Box>
                ) : null}

                <Typography variant="body2" sx={{ mb: 2 }}>
                  Clé manuelle: <strong>{twoFactorSetupData.manualEntryKey}</strong>
                </Typography>

                <TextField
                  fullWidth
                  label="Code 2FA"
                  value={twoFactorVerifyCode}
                  onChange={(e) => setTwoFactorVerifyCode(e.target.value)}
                  placeholder="123456"
                />
              </>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  2FA activée. Conservez ces codes de secours en lieu sûr.
                </Alert>
                <List>
                  {twoFactorBackupCodes.map((code) => (
                    <ListItem key={code}>
                      <ListItemText primary={code} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {twoFactorBackupCodes.length === 0 ? (
              <>
                <Button
                  onClick={() => {
                    setTwoFactorEnabled(false);
                    setTwoFactorSetupDialogOpen(false);
                  }}
                >
                  Annuler
                </Button>
                <Button onClick={handleConfirmEnableTwoFactor} variant="contained" disabled={twoFactorLoading}>
                  {twoFactorLoading ? 'Activation...' : 'Activer la 2FA'}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setTwoFactorSetupDialogOpen(false);
                  setTwoFactorVerifyCode('');
                }}
                variant="contained"
              >
                Terminer
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default Settings;